/**
 * The context a maître d'AI conversation is about (filtering audit Phase C).
 *
 * A conversation records the view context it started in (the venues,
 * period, comparison, statuses, sources and shifts, the screen and its own
 * window) on the server (`opsCommand/getConversationContext` and
 * `setConversationContext`) and in its thread. Its row, its answers and
 * their receipts follow that context, not the screen's: expanding the panel
 * or switching screens keeps it. When the screen shows something else, the
 * thread asks once whether to use the new context.
 *
 * Pure helpers; the controller (useMaitreController) owns the state.
 */
import type { AgentScreen } from '../types';
import { DEFAULT_PERF_SETTINGS } from './perfSettings';
import { filterForVenues } from './scopeFilter';
import {
  CONTEXT_PARTS,
  PERF_COMPARE_KIND_OF,
  localToday,
  resolveComparisonWindow,
  type ContextPart,
  type RequestViewContext,
  type ScreenId,
} from './viewContext';

/** What the thread keeps about its context. */
export type ThreadContext = {
  viewContext: RequestViewContext;
  /** The restaurant opened when it was recorded (it resolves the venues). */
  restaurantId: string;
  recordedAt: string;
};

/** The switch maître d'AI offers when a question needs more than the context. */
export type ContextProposal = {
  period?: { start: string; end: string };
  comparison?: { start: string; end: string };
  venues?: { ids: string[]; added: { id: string; name: string }[] };
};

const sorted = (values: readonly string[] | undefined | null) => [...(values ?? [])].sort();

/**
 * What a context counts, as the server compares it
 * (conversation_context.context_parts): two contexts with the same parts
 * count the same.
 */
export function contextParts(vc: RequestViewContext | null | undefined) {
  if (!vc) return null;
  const compare = vc.comparison?.kind ?? 'none';
  const window = vc.window ?? null;
  return {
    venues: { filter: vc.venues?.filter ?? null, ids: sorted(vc.venues?.ids) },
    period: [vc.period?.start ?? null, vc.period?.end ?? null],
    comparison: [
      compare,
      compare === 'none' ? null : (vc.comparison?.start ?? null),
      compare === 'none' ? null : (vc.comparison?.end ?? null),
    ],
    statuses: sorted(vc.filters?.statuses),
    sources: sorted(vc.filters?.sources),
    shifts: sorted(vc.filters?.shifts),
    window: window ? [window.kind, window.start ?? null, window.end ?? null] : null,
  };
}

export function contextKey(vc: RequestViewContext | null | undefined): string {
  return JSON.stringify(contextParts(vc));
}

export function sameContext(a: RequestViewContext | null | undefined, b: RequestViewContext | null | undefined) {
  return contextKey(a) === contextKey(b);
}

/** The parts the person can change that differ between two contexts. */
export function differingParts(conv: RequestViewContext, screen: RequestViewContext): ContextPart[] {
  const a = contextParts(conv)!;
  const b = contextParts(screen)!;
  const same = (x: unknown, y: unknown) => JSON.stringify(x) === JSON.stringify(y);
  return CONTEXT_PARTS.filter(part => {
    if (part === 'venues') return !same(a.venues.ids, b.venues.ids);
    return !same(a[part], b[part]);
  });
}

/** The maître d'AI screen a view-context screen is. */
export function agentScreenOf(screen: ScreenId | null | undefined): AgentScreen {
  if (!screen) return 'assistant';
  if (screen.startsWith('growth:')) return 'growth';
  if (screen === 'performance-drill') return 'performance';
  if (screen === 'forecast') return 'horizon';
  return screen as AgentScreen;
}

/**
 * The context maître d'AI offered to switch to: the conversation's, with the
 * period (as dates), the comparison (custom dates, or the same kind for the
 * new period) and the venues the question needs. The period then applies to
 * the conversation even when its screen (Live, Forecast) didn't use one.
 */
export function proposalContext(conv: RequestViewContext, proposal: ContextProposal): RequestViewContext {
  let next: RequestViewContext = { ...conv, uses: { ...conv.uses } };
  if (proposal.period) {
    const { start, end } = proposal.period;
    const period = { ...conv.period, kind: 'range' as const, start, end, inProgress: end >= localToday() };
    const compareKind = proposal.comparison
      ? 'custom'
      : PERF_COMPARE_KIND_OF[conv.comparison.kind] ?? 'yoy_weekday';
    const comparison = proposal.comparison
      ? { kind: 'custom' as const, start: proposal.comparison.start, end: proposal.comparison.end, explicit: true }
      : resolveComparisonWindow(
          {
            ...DEFAULT_PERF_SETTINGS,
            aggregation: conv.period.aggregation,
            frameCount: conv.period.frameCount,
            frameUnit: conv.period.frameUnit,
            dateStart: start,
            dateEnd: end,
            compareKind,
            compareExplicit: true,
            ...(conv.comparison.kind === 'custom' && conv.comparison.start && conv.comparison.end
              ? { compareDateStart: conv.comparison.start, compareDateEnd: conv.comparison.end }
              : {}),
          },
          period,
        );
    next = {
      ...next,
      period,
      comparison: { ...comparison, explicit: true },
      uses: { ...next.uses, period: true, comparison: next.uses.comparison || Boolean(proposal.comparison) },
    };
  }
  if (proposal.venues?.ids.length) {
    const ids = [...new Set(proposal.venues.ids)];
    const names = proposal.venues.added.map(v => v.name);
    next = {
      ...next,
      venues: {
        ...next.venues,
        ids,
        filter: filterForVenues(ids),
        origin: 'picker',
        label: names.length ? [next.venues.label, ...names].filter(Boolean).join(', ') : next.venues.label,
      },
      focus: null,
    };
  }
  return next;
}
